import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance with auth interceptor
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ==================== API CALLS THAT MIGHT BE NOT RECIVED IN THE REAL TIME ====================
export const refundApi = {
  getAllRefundHistory: async () => {
    try {
      const response = await apiClient.get(
        "/food/api/refundhistory/all"
      );

      console.log("Refund History:", response.data);

      return response.data;
    } catch (error) {
      console.error("getAllRefundHistory Error:", error);
      throw error;
    }
  },

  getRefundByOrderId: async (refundId) => {
    const response = await apiClient.get(`/food/api/refundhistory/get/order/${refundId}`);
    return response.data;
  },

  processOnlineRefund: async (paymentId) => {
    const response = await apiClient.post("/food/api/payments/refund", {
      payment_id: paymentId,
    });
    return response.data;
  },

  updateRefundStatus: async (refundId, status) => {
    const response = await apiClient.put(`/food/api/refundhistory/update/${refundId}/${status}`);
    return response.data;
  },
};

// ==================== DATA PROCESSING ==================== data of the data code 
export const refundDataProcessor = {
  filterPaymentMethods: (data) => {
    return data.filter(
      item => item.paymentMethod === "Online_Payment" ||
        item.paymentMethod === "Maamaas_Wallet"
    );
  },

  addSerialNumbers: (data) => {
    return data.map((item, index) => ({ ...item, sno: index + 1 }));
  },

  filterData: (data, searchType, searchQuery, selectedStatusFilter, filterDate) => {
    if (!searchQuery.trim() && !selectedStatusFilter && !filterDate) return data;

    const query = searchQuery.toLowerCase().trim();

    return data.filter(item => {
      const searchMatch = !searchQuery.trim() ||
        (item[searchType] && item[searchType].toString().toLowerCase().includes(query));

      const statusMatch = !selectedStatusFilter || item.status === selectedStatusFilter;

      const dateMatch = !filterDate ||
        refundDateFormatter.formatDateForDisplay(item.cancelledAt) === filterDate;

      return searchMatch && statusMatch && dateMatch;
    });
  },

  getUniqueStatuses: (data) => {
    return [...new Set(data.map((item) => item.status))].sort();
  },
};

// ==================== DATE FORMATTING ====================
export const refundDateFormatter = {
  formatDateForDisplay: (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return "-";
    }
  },

  formatDateTimeForDisplay: (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
      return "-";
    }
  },

  formatTimeForDisplay: (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${hours}:${minutes}:${seconds}`;
    } catch {
      return "-";
    }
  },

  formatDateForInput: (dateString) => {
    if (!dateString) return "";
    try {
      if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  }
};

// ==================== EXPORT FUNCTIONALITY ====================
export const refundExport = {
  exportToCSV: (data, filename = "cancellations_and_refunds.csv") => {
    if (data.length === 0) {
      alert("There is no data to export.");
      return;
    }

    const escapeCsvField = (field) => {
      const str = String(field ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = [
      "S.NO", "Order ID", "User ID", "Name", "Request Date",
      "Request Time", "Amount", "Transaction ID", "Refund ID",
      "Payment Method", "Status", "Cancelled At"
    ];

    const csvRows = data.map((row) => [
      row.sno || '',
      row.orderId || '',
      row.userId || '',
      row.userName || '',
      row.cancelledAt ? refundDateFormatter.formatDateForDisplay(row.cancelledAt) : '',
      row.cancelledAt ? refundDateFormatter.formatTimeForDisplay(row.cancelledAt) : '',
      `₹${Number(row.price || 0).toFixed(2)}`,
      row.transactionId || '',
      row.refundId || '',
      row.paymentMethod || '',
      row.status || '',
      row.cancelledAt ? refundDateFormatter.formatDateTimeForDisplay(row.cancelledAt) : ''
    ]);

    const csvString = [
      headers.map(escapeCsvField).join(','),
      ...csvRows.map(row => row.map(escapeCsvField).join(','))
    ].join('\r\n');

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

// ==================== UI CONSTANTS ====================
export const refundUIConstants = {
  searchOptions: [
    { value: "userId", label: "User ID" },
    { value: "orderId", label: "Order ID" },
    { value: "userName", label: "User Name" },
    { value: "transactionId", label: "Transaction ID" },
    { value: "refundId", label: "Refund ID" }
  ],

  getPlaceholderText: (searchType) => {
    const placeholders = {
      userId: "Search User ID",
      orderId: "Search Order ID",
      userName: "Search User Name",
      transactionId: "Search Transaction ID",
      refundId: "Search Refund ID"
    };
    return placeholders[searchType] || "Search...";
  },

  getStatusBadgeClass: (status) => {
    const classes = {
      pending: "bg-warning text-dark",
      approved: "bg-success",
      rejected: "bg-danger"
    };
    return classes[status?.toLowerCase()] || "bg-secondary";
  },

  tableStyles: {
    th: {
      border: "1px solid #ddd",
      textAlign: "center",
      fontWeight: "bold",
      backgroundColor: "#b15cd6",
      color: "white",
    },
    td: {
      border: "1px solid #ddd",
      textAlign: "center",
    },
    input: {
      width: "100%",
      padding: "8px",
      borderRadius: "6px",
      border: "1px solid #ccc",
      marginTop: "4px",
      backgroundColor: "#f9f9f9",
      fontSize: "15px",
    }
  }
};